import React, { useState, useEffect } from 'react';

const MONTHS_PT = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * CalendarCard — calendário premium dentro de card branco arredondado.
 *
 * Props:
 *   selectedDate         - data selecionada (YYYY-MM-DD)
 *   onSelect             - callback ao clicar em um dia
 *   availableDaysOfWeek  - array de números (0=Dom, 1=Seg, ..., 6=Sáb)
 *   rangeData            - { "YYYY-MM-DD": { hasAvailability: bool, ... } }
 *   rangeLoading         - boolean: true enquanto carrega dados do mês
 *   onMonthChange        - callback(year, month) ao mudar o mês visível
 */
function CalendarCard({ selectedDate, onSelect, availableDaysOfWeek, rangeData, rangeLoading, onMonthChange }) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Fallback: Seg-Sex
    const allowedDays = Array.isArray(availableDaysOfWeek) && availableDaysOfWeek.length > 0
        ? availableDaysOfWeek
        : [1, 2, 3, 4, 5];

    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    // Ao montar ou mudar de mês, dispara o carregamento do range
    useEffect(() => {
        if (onMonthChange) {
            onMonthChange(viewYear, viewMonth);
        }
    }, [viewYear, viewMonth]); // eslint-disable-line react-hooks/exhaustive-deps

    const prevMonth = () => {
        if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
        else setViewMonth(m => m + 1);
    };

    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    // Verificar se temos dados de range para este mês
    const hasRangeData = rangeData && Object.keys(rangeData).length > 0;

    return (
        <div className="ps-calendar">
            <div className="ps-calendar__nav">
                <button className="ps-calendar__nav-btn" onClick={prevMonth} aria-label="Mês anterior">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <span className="ps-calendar__month">
                    {MONTHS_PT[viewMonth]} {viewYear}
                    {rangeLoading && <span className="ps-spinner ps-calendar__loading" aria-label="Carregando" />}
                </span>
                <button className="ps-calendar__nav-btn" onClick={nextMonth} aria-label="Próximo mês">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            </div>

            <div className="ps-calendar__grid">
                {DAYS_PT.map(d => (
                    <div key={d} className="ps-calendar__day-label">{d}</div>
                ))}
                {cells.map((day, idx) => {
                    if (!day) return <div key={`e-${idx}`} className="ps-calendar__day ps-calendar__day--empty" />;
                    const cellDate = new Date(viewYear, viewMonth, day);
                    const isPast = cellDate < today;
                    const dayOfWeek = cellDate.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
                    const isUnavailableDay = !allowedDays.includes(dayOfWeek);

                    const dateStr = toDateStr(cellDate);

                    // Verificar disponibilidade via rangeData (se carregado)
                    let noAvailability = false;
                    if (hasRangeData && !isPast && !isUnavailableDay) {
                        const dayData = rangeData[dateStr];
                        if (dayData && !dayData.hasAvailability) {
                            noAvailability = true; // Dia lotado — sem horários disponíveis
                        }
                    }

                    // Dias passados e fora do funcionamento são totalmente desabilitados (sem clique)
                    // Dias lotados (noAvailability) ficam com estilo diferente MAS são clicáveis
                    const isHardDisabled = isPast || isUnavailableDay;
                    const isSelected = dateStr === selectedDate;

                    let cls = 'ps-calendar__day';
                    if (isHardDisabled) {
                        cls += ' ps-calendar__day--disabled';
                    } else if (noAvailability) {
                        cls += ' ps-calendar__day--full'; // Estilo "lotado" mas clicável
                        if (isSelected) cls += ' ps-calendar__day--selected';
                    } else if (isSelected) {
                        cls += ' ps-calendar__day--selected';
                    }

                    return (
                        <div
                            key={day}
                            className={cls}
                            onClick={() => !isHardDisabled && onSelect(dateStr)}
                            title={noAvailability ? 'Sem horários disponíveis neste dia' : ''}
                        >
                            {day}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * StepDate — Step 2
 * Seleção de data com calendário premium.
 * Dias sem disponibilidade ficam desabilitados automaticamente.
 */
export default function StepDate({ selectedDate, onSelect, onNext, onBack, availableDaysOfWeek, rangeData, rangeLoading, onMonthChange }) {
    return (
        <div className="ps-step-content">
            <h2 className="ps-step-title">Escolha a data</h2>
            <p className="ps-step-subtitle">Selecione uma data disponível.</p>

            <CalendarCard
                selectedDate={selectedDate}
                onSelect={onSelect}
                availableDaysOfWeek={availableDaysOfWeek}
                rangeData={rangeData || {}}
                rangeLoading={rangeLoading || false}
                onMonthChange={onMonthChange}
            />

            {/* Info card */}
            <div className="ps-info-card">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ED3680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>Só mostramos os dias com horários disponíveis para você.</span>
            </div>

            <div className="ps-actions">
                <button className="ps-btn ps-btn--secondary" onClick={onBack}>
                    ← Voltar
                </button>
                <button
                    className="ps-btn ps-btn--primary"
                    disabled={!selectedDate}
                    onClick={onNext}
                >
                    Continuar
                </button>
            </div>

            <p className="ps-trust-note">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Seus dados estão protegidos
            </p>
        </div>
    );
}
